'use client';

import { useState, useEffect, useRef, useId } from 'react';
import { cardOpensProductPage, serviceBillableArea, usesConfigurator } from '@/core/utils/product-action';
import { useSession } from 'next-auth/react';
import { Button } from '@/core/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/core/components/ui/card';
import { Input } from '@/core/components/ui/input';
import Link, { useRouter } from '@/core/i18n/navigation';
import { couponErrorMessage } from '@/core/utils/couponError';
import { Trash, ShoppingCart, Tag, X, Info, Building2, ChevronLeft, ChevronRight, Sparkles, Gift } from 'lucide-react';
import { fetchAPI } from '@/core/api/client';
import { toast } from '@/core/utils/toast';
import { useTranslation } from '@/core/hooks/useTranslation';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import { getAbsoluteMediaUrl, getPublicMediaUrl, toOptimizableSrc } from '@/core/utils/media';
import { Skeleton } from '@/core/components/ui/skeleton';
import { trackEvent, trackViewCart, trackRemoveFromCart, feedItemId } from '@/core/utils/analytics';
import { BundleSelectionList, parseBundleSummary } from '@/core/components/BundleSelectionList';
import { evaluateConfigurator } from '@/core/components/Builder/configurator-utils';
import { formatPrice } from '@/core/utils/currency';
import { getOrCreateSeatSessionToken } from '@/lib/seat-map-utils';
import { revalidateCartSeats } from '@/lib/cart';
import { LoyaltyEarnPreview } from '@/core/components/loyalty/LoyaltyEarnPreview';
import { useSiteConfig } from '@/core/components/providers/SiteConfigProvider';
import { applyB2BTiers, cartNetValue, useB2BMinOrderValue } from '@/core/utils/b2b-pricing';

/** Builds the ?kupon=&punkty= query string carried from cart to checkout, mirroring how the
 *  discount code is passed — both are re-validated server-side against the final cart in
 *  CheckoutBlock, this is only a hand-off of "what the customer picked in the cart". */
function buildCheckoutQuery(
    appliedCoupon: { code: string } | null,
    appliedPoints: { points: number } | null
): string {
    const params = new URLSearchParams();
    if (appliedCoupon) params.set('kupon', appliedCoupon.code);
    if (appliedPoints) params.set('punkty', String(appliedPoints.points));
    const qs = params.toString();
    return qs ? `?${qs}` : '';
}


export interface CartBlockProps {
    isAdmin?: boolean;
    settings?: any;
    viewport?: 'desktop' | 'tablet' | 'mobile';
    styles?: any;
}

export function CartBlock({ isAdmin = false, settings: rawSettings = {}, viewport, styles }: CartBlockProps) {
    const { t, currency, tPath } = useTranslation();
    const router = useRouter();

    // Sizing fields (padding/font-size/border-radius/border-width) are responsive — the editor
    // writes them to styles[viewport], merged over the flat settings object here so every
    // existing `settings.X` read below picks up the per-device value automatically, with a
    // fallback to the old flat key for pages configured before this existed. Field names here
    // (productListPadding, summaryBorderRadius, upsellCardPadding, ...) are specific enough that
    // this blanket merge can't collide with the generic Appearance/Spacing panel's style keys.
    const currentStyles = { ...(styles || {}), ...(viewport ? (styles || {})[viewport] || {} : {}) };
    const settings = { ...rawSettings, ...currentStyles };

    const productCardStyle = {
        backgroundColor: settings.productListBg || undefined,
        borderColor: settings.productListBorderColor || undefined,
        borderWidth: settings.productListBorderWidth !== undefined ? `${settings.productListBorderWidth}px` : undefined,
        borderStyle: settings.productListBorderWidth ? 'solid' : undefined,
        borderRadius: settings.productListBorderRadius !== undefined ? `${settings.productListBorderRadius}px` : undefined,
    };

    const productCardContentStyle = {
        padding: settings.productListPadding !== undefined ? `${settings.productListPadding}px` : undefined,
    };

    const productTitleStyle = {
        color: settings.productListTitleColor || undefined,
        fontSize: settings.productListTitleSize !== undefined ? `${settings.productListTitleSize}px` : undefined,
    };

    const productTextStyle = {
        color: settings.productListTextColor || undefined,
        fontSize: settings.productListTextSize !== undefined ? `${settings.productListTextSize}px` : undefined,
    };

    const summaryCardStyle = {
        backgroundColor: settings.summaryBg || undefined,
        borderColor: settings.summaryBorderColor || undefined,
        borderWidth: settings.summaryBorderWidth !== undefined ? `${settings.summaryBorderWidth}px` : undefined,
        borderStyle: settings.summaryBorderWidth ? 'solid' : undefined,
        borderRadius: settings.summaryBorderRadius !== undefined ? `${settings.summaryBorderRadius}px` : undefined,
    };

    const summaryCardContentStyle = {
        padding: settings.summaryPadding !== undefined ? `${settings.summaryPadding}px` : undefined,
    };

    const summaryTitleStyle = {
        color: settings.summaryTitleColor || undefined,
        fontSize: settings.summaryTitleSize !== undefined ? `${settings.summaryTitleSize}px` : undefined,
    };

    const summaryTextStyle = {
        color: settings.summaryTextColor || undefined,
        fontSize: settings.summaryTextSize !== undefined ? `${settings.summaryTextSize}px` : undefined,
    };

    const summaryBtnStyle = {
        backgroundColor: settings.summaryBtnBg || undefined,
        color: settings.summaryBtnTextColor || undefined,
    };

    const quantityWrapperStyle = settings.quantityBtnBg ? { backgroundColor: settings.quantityBtnBg } : undefined;
    const quantityTextStyle = settings.quantityBtnTextColor ? { color: settings.quantityBtnTextColor } : undefined;

    const attributeBadgeStyle = (settings.attributeBadgeBg || settings.attributeBadgeTextColor) ? {
        backgroundColor: settings.attributeBadgeBg || undefined,
        color: settings.attributeBadgeTextColor || undefined,
    } : undefined;

    const imagePlaceholderStyle = settings.imagePlaceholderBg ? { backgroundColor: settings.imagePlaceholderBg } : undefined;
    const imagePlaceholderTextStyle = settings.imagePlaceholderTextColor ? { color: settings.imagePlaceholderTextColor } : undefined;

    const freeShippingCardStyle = (settings.freeShippingCardBg || settings.freeShippingCardBorderColor) ? {
        backgroundColor: settings.freeShippingCardBg || undefined,
        borderColor: settings.freeShippingCardBorderColor || undefined,
    } : undefined;
    const freeShippingTrackStyle = settings.freeShippingTrackColor ? { backgroundColor: settings.freeShippingTrackColor } : undefined;
    const freeShippingTextStyle = settings.freeShippingTextColor ? { color: settings.freeShippingTextColor } : undefined;

    const couponInputStyle = (settings.couponInputBg || settings.couponInputBorderColor) ? {
        backgroundColor: settings.couponInputBg || undefined,
        borderColor: settings.couponInputBorderColor || undefined,
    } : undefined;
    const couponErrorStyle = settings.couponErrorTextColor ? { color: settings.couponErrorTextColor } : undefined;

    const leasingBoxStyle = (settings.leasingBoxBg || settings.leasingBoxBorderColor) ? {
        backgroundColor: settings.leasingBoxBg || undefined,
        borderColor: settings.leasingBoxBorderColor || undefined,
    } : undefined;
    const leasingLabelStyle = settings.leasingLabelColor ? { color: settings.leasingLabelColor } : undefined;
    const leasingTextStyle = settings.leasingTextColor ? { color: settings.leasingTextColor } : undefined;

    const instanceId = useId();
    const removeIconCls = `cart-remove-icon-${instanceId}`.replace(/:/g, '');
    const removeIconStyle = settings.removeIconColor ? { color: settings.removeIconColor } : undefined;
    const removeIconHoverStyle = settings.removeIconHoverColor
        ? `.${removeIconCls}:hover { color: ${settings.removeIconHoverColor} !important; }`
        : '';

    const [cart, setCart] = useState<any[]>([]);
    const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());
    const [isMounted, setIsMounted] = useState(false);
    const [previewEmptyCart, setPreviewEmptyCart] = useState(false);
    const searchParams = useSearchParams();
    const siteConfig = useSiteConfig();

    // Coupon state
    const [couponInput, setCouponInput] = useState('');
    const [appliedCoupon, setAppliedCoupon] = useState<{ code: string; discount: number; type: string; freeShipping?: boolean; gift?: { name: string } | null } | null>(null);
    const [couponError, setCouponError] = useState('');
    const [couponLoading, setCouponLoading] = useState(false);

    // Loyalty points state
    const { data: session } = useSession();
    const b2bMinOrder = useB2BMinOrderValue(currency);
    const [loyaltyBalance, setLoyaltyBalance] = useState(0);
    const [pointsInput, setPointsInput] = useState('');
    const [appliedPoints, setAppliedPoints] = useState<{ points: number; discount: number } | null>(null);
    const [pointsError, setPointsError] = useState('');
    const [pointsLoading, setPointsLoading] = useState(false);

    // Left column inline upsell block state
    const [upsellProducts, setUpsellProducts] = useState<any[]>([]);
    const [upsellPage, setUpsellPage] = useState(0);
    const [hoveredUpsellId, setHoveredUpsellId] = useState<any>(null);

    const upsellLimit = settings.upsellLimit !== undefined ? settings.upsellLimit : 12;
    const upsellQueryType = settings.upsellQueryType || 'latest';
    const upsellCategoryId = settings.upsellCategoryId;
    const upsellManualProductIds = settings.upsellManualProductIds || [];

    useEffect(() => {
        if (settings.showUpsellBlock === false) return;

        let endpoint = `/products?limit=${Math.max(12, upsellLimit)}`;
        if (upsellQueryType === 'manual' && upsellManualProductIds.length > 0) {
            endpoint = `/products?ids=${upsellManualProductIds.join(',')}&limit=${upsellManualProductIds.length}`;
        } else if (upsellQueryType === 'category' && upsellCategoryId) {
            endpoint = `/products?category_id=${upsellCategoryId}&limit=${Math.max(12, upsellLimit)}`;
        }

        fetchAPI(endpoint)
            .then((res: any) => {
                let list = Array.isArray(res) ? res : res?.items || [];
                if (upsellQueryType === 'manual' && upsellManualProductIds.length > 0) {
                    // Sort items strictly according to user-selected manual order
                    const idMap = new Map(list.map((item: any) => [String(item.id), item]));
                    list = upsellManualProductIds
                        .map((id: any) => idMap.get(String(id)))
                        .filter(Boolean);
                }
                setUpsellProducts(list);
            })
            .catch(() => setUpsellProducts([]));
    }, [settings.showUpsellBlock, upsellLimit, upsellQueryType, upsellCategoryId, JSON.stringify(upsellManualProductIds)]);

    const handleQuickAddUpsell = (prod: any) => {
        if (cardOpensProductPage(prod)) {
            router.push(`/produkty/${prod.slug}`);
            return;
        }
        let savedCart = JSON.parse(localStorage.getItem('stag-cart') || '[]');
        const existingIndex = savedCart.findIndex((i: any) => i.id === prod.id);
        if (existingIndex > -1) {
            savedCart[existingIndex].quantity += 1;
        } else {
            savedCart.push({
                id: prod.id,
                product_type: prod.product_type,
                name: prod.name,
                price: prod.sales_price ?? prod.price,
                quantity: 1,
                image_url: prod.image_url,
            });
        }
        localStorage.setItem('stag-cart', JSON.stringify(savedCart));
        window.dispatchEvent(new Event('cart-updated'));
        toast.success(t('cart.item_added', 'Dodano {name} do koszyka!').replace('{name}', prod.name));
        setCart(savedCart);
    };

    useEffect(() => {
        if (!siteConfig?.loyalty_enabled || !session?.user) {
            setLoyaltyBalance(0);
            return;
        }
        fetchAPI('/loyalty/me').then((res: any) => {
            setLoyaltyBalance(res?.balance || 0);
        }).catch(() => setLoyaltyBalance(0));
    }, [siteConfig?.loyalty_enabled, session?.user]);

    useEffect(() => {
        setIsMounted(true);
        let savedCart = JSON.parse(localStorage.getItem('stag-cart') || '[]');
        if (isAdmin && savedCart.length === 0) {
            savedCart = [
                {
                    id: 9999,
                    name: 'Przykładowy Produkt Premium (Podgląd)',
                    price: 299.99,
                    quantity: 1,
                    image: null,
                    attributes: { 'Kolor': 'Srebrny', 'Rozmiar': 'Uniwersalny' }
                }
            ];
        }

        // Drop seat-ticket items whose 2h cart hold has almost certainly expired
        // server-side — keeps the cart honest without needing a background job.
        const now = Date.now();
        const expiredSeats = savedCart.filter((i: any) => i.seat_id && i.held_until && i.held_until < now);
        if (expiredSeats.length > 0) {
            savedCart = savedCart.filter((i: any) => !(i.seat_id && i.held_until && i.held_until < now));
            localStorage.setItem('stag-cart', JSON.stringify(savedCart));
            toast.error(
                expiredSeats.length === 1
                    ? t('cart.seat_hold_expired_one', 'Czas rezerwacji miejsca wygasł — zostało usunięte z koszyka.')
                    : t('cart.seat_hold_expired_many', 'Czas rezerwacji {n} miejsc wygasł — zostały usunięte z koszyka.').replace('{n}', String(expiredSeats.length))
            );
        }

        // Fetch fresh product details to ensure prices/names match the current currency/locale
        const fetchFreshCartDetails = async () => {
            if (isAdmin) {
                setCart(savedCart);
                return;
            }
            // Seats taken by another shopper / blocked by the box office since they were added
            // would only fail at payment — re-hold them now and drop the lost ones up front.
            const { items: revalidated, lost: lostSeats } = await revalidateCartSeats(savedCart);
            savedCart = revalidated;
            if (lostSeats.length > 0) {
                toast.error(
                    lostSeats.length === 1
                        ? t('cart.seat_taken_one', 'Jedno z miejsc w koszyku zostało w międzyczasie zajęte — usunęliśmy je z koszyka.')
                        : t('cart.seat_taken_many', '{n} miejsc w koszyku zostało w międzyczasie zajętych — usunęliśmy je z koszyka.').replace('{n}', String(lostSeats.length))
                );
            }
            try {
                const freshCart = await Promise.all(savedCart.map(async (item: any) => {
                    try {
                        const product = await fetchAPI(`/products/${item.id}`);
                        if (product) {
                            let price = product.sales_price !== null ? product.sales_price : product.price;
                            let name = product.name;
                            let image = item.image;

                            // If variant is selected, find the correct variant price
                            let variant: any = null;
                            if (item.variant_id && product.variants) {
                                variant = product.variants.find((v: any) => v.id === Number(item.variant_id));
                                if (variant) {
                                    price = variant.sales_price !== null ? variant.sales_price : variant.price;
                                    name = product.name + ` (${Object.values(variant.attributes).join(', ')})`;
                                }
                            }

                            // Ticket products: Product.price is a placeholder — the real
                            // price lives per-event/per-section. Re-derive from there
                            // instead of overwriting with the placeholder.
                            if (item.event_details_id) {
                                try {
                                    const eventsList = await fetchAPI(`/products/${item.id}/events`);
                                    const ev = (eventsList || []).find((e: any) => e.id === item.event_details_id);
                                    const sec = ev?.sections?.find((s: any) => s.name === item.ticket_section);
                                    price = sec ? sec.price : item.price;
                                } catch {
                                    price = item.price;
                                }
                            }
                            // Configurable products: `price` here is only the product's base
                            // price, freshly converted server-side to the active currency — it
                            // doesn't include the attribute price deltas from item.custom_config.
                            // Re-run the same pricing logic the configurator itself uses
                            // (evaluateConfigurator) against a fresh /configurator fetch so the
                            // delta is also priced in the currently active currency, instead of
                            // reusing item.price, which is frozen at whatever currency was active
                            // when the item was added to the cart.
                            if (item.custom_config) {
                                try {
                                    const configuratorData = await fetchAPI(`/products/${item.id}/configurator`);
                                    if (configuratorData?.product_attributes) {
                                        const selections: Record<number, number[]> = {};
                                        for (const entry of item.custom_config) {
                                            selections[entry.attribute_id] = entry.value_ids;
                                        }
                                        const evaluation = evaluateConfigurator(
                                            selections,
                                            configuratorData.product_attributes,
                                            configuratorData.conditions || [],
                                            currency
                                        );
                                        price = Math.round((price + evaluation.priceDelta) * 100) / 100;
                                    } else {
                                        price = item.price;
                                    }
                                } catch {
                                    price = item.price;
                                }
                            }

                            // Per m²: the line is one measured piece — rate × its billable area (same as checkout
                            // and create_order); the name keeps its "(200 × 300 cm)" from the product page.
                            if (item.service_measure && product.product_type === 'service' && product.service_config?.pricing_type === 'area') {
                                price = Math.round(price * serviceBillableArea(product, item.service_measure) * 100) / 100;
                                name = item.name || name;
                            }

                            // Progi ilościowe B2B (D9) — tylko zwykłe linie; bilety, konfiguratory i usługi
                            // na m² mają własną cenę. Cena bazowa zostaje do przeliczenia po zmianie ilości.
                            const b2bTiers = (variant || product).b2b_tiers;
                            const isPlainLine = !item.event_details_id && !item.custom_config && !item.service_measure;
                            const b2bFields = isPlainLine && b2bTiers?.length
                                ? { b2b_base_price: price, b2b_tiers: b2bTiers }
                                : { b2b_base_price: undefined, b2b_tiers: undefined };

                            const mainImage = product.images?.find((img: any) => img.is_main) || product.images?.[0];
                            if (mainImage) {
                                image = getPublicMediaUrl(mainImage.path);
                            }

                            const categoryName = product.category_path && product.category_path.length > 0
                                ? product.category_path[product.category_path.length - 1]?.name
                                : (product.categories && product.categories.length > 0 ? product.categories[product.categories.length - 1]?.name : '');

                            // A flat-rate service (ryczałt) is one charge whatever the count — show it as a
                            // fixed single line (no stepper), the same way one-off products are.
                            const isFlatService = product.product_type === 'service' && product.service_config?.pricing_type === 'flat';
                            // Laundry by weight: the quantity is kilograms, so the line reads "12 zł / kg" and "6 kg".
                            const isWeightService = product.product_type === 'service' && product.service_config?.pricing_type === 'weight';
                            return applyB2BTiers({
                                ...item,
                                qty_unit: isWeightService ? 'kg' : undefined,
                                ...(isFlatService ? { quantity: 1 } : {}),
                                ...b2bFields,
                                tax_rate: product.tax_rate ?? 23,
                                name,
                                price,
                                image,
                                slug: product.slug,
                                sku: variant?.sku || product.sku || null,
                                feed_id: feedItemId(product, variant),
                                category_name: categoryName || '',
                                is_one_off: !!product.is_one_off || !!item.seat_id || isFlatService,
                                requires_shipping: product.requires_shipping !== false
                            });
                        }
                    } catch (err) {
                        console.error(`Failed to fetch fresh details for product ${item.id}`, err);
                    }
                    return item;
                }));
                
                localStorage.setItem('stag-cart', JSON.stringify(freshCart));
                setCart(freshCart);
                trackViewCart(freshCart, currency || 'PLN');
                window.dispatchEvent(new Event('cart-updated'));
            } catch (err) {
                console.error("Error refreshing cart details:", err);
                setCart(savedCart);
            }
        };

        if (savedCart.length > 0) {
            fetchFreshCartDetails();
        } else {
            setCart([]);
        }

    }, []);

    useEffect(() => {
        const handleCartUpdate = () => {
            const savedCart = JSON.parse(localStorage.getItem('stag-cart') || '[]');
            setCart(savedCart);
        };

        window.addEventListener('cart-updated', handleCartUpdate);
        return () => window.removeEventListener('cart-updated', handleCartUpdate);
    }, []);

    // "Koszyki klientów" sync lives in CartTrackingSync (store layout) — it follows every cart change.

    const [leasenowCartData, setLeasenowCartData] = useState<any>(null);
    const lastCartKeyRef = useRef<string>('');

    useEffect(() => {
        if (settings.showLeasingOption === false || !siteConfig?.leasenow_enabled || cart.length === 0) {
            lastCartKeyRef.current = '';
            setLeasenowCartData(null);
            return;
        }

        const cartKey = cart.map(i => `${i.id}-${i.variant_id || 'b'}-${i.price}-${i.quantity}`).join('|');
        if (lastCartKeyRef.current === cartKey) {
            return;
        }
        lastCartKeyRef.current = cartKey;

        let cancelled = false;

        async function checkCartLeasing() {
            try {
                const products = cart.map(item => {
                    const priceGross = Number(item.price) || 0;
                    const taxRate = typeof item.tax_rate === 'number' ? item.tax_rate : (parseFloat(item.tax_rate || item.taxRate || '23') || 23.0);
                    const priceNet = Math.round((priceGross / (1 + taxRate / 100.0)) * 100) / 100;
                    const itemId = item.variant_id ? `${item.id}-${item.variant_id}` : `${item.id}`;
                    return {
                        id: itemId,
                        name: item.name,
                        quantity: item.quantity || 1,
                        valueNet: priceNet,
                        valueVatPercent: taxRate,
                        url: typeof window !== 'undefined' ? `${window.location.origin}/produkty/${item.slug}` : '',
                        state: 'NEW',
                        categoryId: item.category_name || 'Sprzęt'
                    };
                });

                const res = await fetchAPI('/payments/leasenow/check-availability', {
                    method: 'POST',
                    body: JSON.stringify({
                        products,
                        currencyIsoName: currency || 'PLN'
                    })
                });

                if (!cancelled) {
                    setLeasenowCartData(res);
                }
            } catch (err) {
                console.error("Error checking cart Lease Now availability:", err);
                if (!cancelled) {
                    setLeasenowCartData(null);
                }
            }
        }

        checkCartLeasing();

        return () => {
            cancelled = true;
        };
    }, [cart, settings.showLeasingOption, siteConfig?.leasenow_enabled, currency]);

    const updateQuantity = (cartItemId: string, delta: number) => {
        const newCart = cart.map(item => {
            const currentId = item.cartItemId ?? String(item.id);
            if (currentId === cartItemId) {
                if (item.is_one_off) return item;
                // Offer / live-sale lines carry max_quantity: the order is rejected above the reserved amount.
                const cap = Number(item.max_quantity) || Infinity;
                // A D2D service's minimum load ("min. 4 kg") — less is billed as that much anyway.
                const floor = Math.max(1, Number(item.min_quantity) || 1);
                const currentQty = Math.min(cap, Math.max(floor, (Number(item.quantity) || 1) + delta));
                return applyB2BTiers({ ...item, quantity: currentQty });
            }
            return item;
        });
        localStorage.setItem('stag-cart', JSON.stringify(newCart));
        setCart(newCart);
        window.dispatchEvent(new Event('cart-updated'));
    };

    const removeItem = (cartItemId: string) => {
        setRemovingIds(prev => new Set(prev).add(cartItemId));
        setTimeout(() => {
            setRemovingIds(prev => { const s = new Set(prev); s.delete(cartItemId); return s; });
            const removed = cart.find(item => (item.cartItemId ?? String(item.id)) === cartItemId);
            const newCart = cart.filter(item => (item.cartItemId ?? String(item.id)) !== cartItemId);
            if (removed) trackRemoveFromCart([removed], currency || 'PLN');
            localStorage.setItem('stag-cart', JSON.stringify(newCart));
            setCart(newCart);
            window.dispatchEvent(new Event('cart-updated'));

            // Seat tickets hold a physical seat — free it immediately instead of
            // making other shoppers wait out the full cart-hold TTL.
            if (removed?.seat_id && removed?.event_details_id) {
                const sessionToken = getOrCreateSeatSessionToken();
                if (sessionToken && sessionToken !== 'ssr') {
                    fetchAPI('/seats/hold', {
                        method: 'DELETE',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ event_details_id: removed.event_details_id, seat_id: removed.seat_id, session_token: sessionToken }),
                    }).catch(() => {});
                }
            }
        }, 220);
    };

    const applyCoupon = async (overrideCode?: string, silent = false) => {
        const codeToValidate = (overrideCode || couponInput).trim();
        if (!codeToValidate) return;
        setCouponLoading(true);
        setCouponError('');
        try {
            const res = await fetchAPI('/discount-codes/validate', {
                method: 'POST',
                body: JSON.stringify({
                    code: codeToValidate,
                    cart_total: subtotal,
                    currency,
                    product_ids: cart.map((i: any) => i.id),
                    // Per-line totals: a product/category-scoped code discounts only its own
                    // lines, and "2+1" codes need the unit prices.
                    items: cart.map((i: any) => ({ product_id: i.id, quantity: i.quantity, line_total: Math.round(i.price * i.quantity * 100) / 100 })),
                }),
            });
            if (res?.valid) {
                const upperCode = codeToValidate.toUpperCase();
                setAppliedCoupon({ code: upperCode, discount: res.discount_amount, type: res.code_type, freeShipping: !!res.free_shipping, gift: res.gift || null });
                setCouponInput('');
                localStorage.setItem('stag_discount_code', upperCode);
                sessionStorage.setItem('stag_discount_code', upperCode);
                if (!silent) {
                    if (res.gift) {
                        toast.success(`${t('cart.code_applied_gift', 'Kod aktywny — do zamówienia dodamy gratis:')} ${res.gift.name}`);
                    } else if (res.free_shipping && !(res.discount_amount > 0)) {
                        toast.success(t('cart.code_applied_free_shipping', 'Kod aktywny — dostawa gratis!'));
                    } else {
                        toast.success(`${t('cart.code_applied', 'Kod rabatowy został pomyślnie naliczony!')} - ${formatPrice(res.discount_amount, currency)}`);
                    }
                }
            } else {
                // A re-check after a cart change (e.g. a "2+1" code once a unit is removed) drops
                // the stale discount; the code stays saved, so it comes back if the cart qualifies again.
                if (silent) setAppliedCoupon(null);
                setCouponError(couponErrorMessage(res, t, t('cart.coupon_invalid', 'Nieprawidłowy kod rabatowy')));
            }
        } catch {
            setCouponError(t('cart.coupon_error', 'Nie udało się zweryfikować kodu'));
        } finally {
            setCouponLoading(false);
        }
    };

    const removeCoupon = () => {
        setAppliedCoupon(null);
        setCouponError('');
        localStorage.removeItem('stag_discount_code');
        sessionStorage.removeItem('stag_discount_code');
    };

    const applyPoints = async () => {
        const points = parseInt(pointsInput, 10);
        if (!points || points <= 0) return;
        setPointsLoading(true);
        setPointsError('');
        try {
            const res = await fetchAPI('/loyalty/redeem-preview', {
                method: 'POST',
                body: JSON.stringify({ points, cart_total: subtotal - (appliedCoupon?.discount ?? 0) }),
            });
            if (res?.valid) {
                // Backend caps points_used to what the cart total actually needs — a customer
                // requesting more points than the order is worth only spends what's needed,
                // never the full requested amount (see shop/logic/loyalty.py redeem_points_preview).
                setAppliedPoints({ points: res.points_used, discount: res.discount_amount });
                setPointsInput('');
                if (res.capped) {
                    toast.success(res.message || `${t('cart.points_capped', 'Wykorzystano tylko tyle punktów, ile pokrywa zamówienie')} - ${res.points_used} pkt (${formatPrice(res.discount_amount, currency)})`);
                } else {
                    toast.success(`${t('cart.points_applied', 'Punkty zostały wykorzystane!')} - ${formatPrice(res.discount_amount, currency)}`);
                }
            } else {
                setPointsError(res?.message || t('cart.points_invalid', 'Nie można wykorzystać podanej liczby punktów'));
            }
        } catch {
            setPointsError(t('cart.points_error', 'Nie udało się zweryfikować punktów'));
        } finally {
            setPointsLoading(false);
        }
    };

    const removePoints = () => {
        setAppliedPoints(null);
        setPointsError('');
    };

    // Auto-apply saved voucher from URL or localStorage/sessionStorage. Must run unconditionally
    // (before the `!isMounted` early return below) — a hook placed after a conditional return
    // fires on some renders and not others, which React rejects (error #310, "rendered more hooks
    // than during the previous render") the moment `isMounted` flips true.
    useEffect(() => {
        if (!isMounted || cart.length === 0) return;
        const cartSubtotal = cart.reduce((acc, item) => acc + (item.price * item.quantity), 0);
        if (cartSubtotal <= 0) return;
        // An applied code is re-checked on every cart change — scoped and "2+1" discounts depend
        // on which lines are in the cart, not just on its total.
        const urlCoupon = searchParams?.get('kupon');
        const savedCoupon = localStorage.getItem('stag_discount_code') || sessionStorage.getItem('stag_discount_code');
        const codeToTry = appliedCoupon?.code || urlCoupon || savedCoupon;
        if (codeToTry) {
            // Silent: this just restores a coupon already applied in a previous visit/session —
            // re-showing "kod naliczony!" on every remount of the cart page (e.g. after a trip to
            // Moje Konto and back) reads as if a NEW discount was stacked on top.
            applyCoupon(codeToTry, true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMounted, cart]);

    if (!isMounted) {
        return (
            <div className="container py-10 space-y-6 animate-in fade-in duration-300">
                <Skeleton className="h-8 w-48 rounded-lg" />
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 space-y-4">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <div key={i} className="flex items-center gap-4 p-4 border border-slate-100 rounded-2xl bg-white shadow-sm">
                                <Skeleton className="h-20 w-20 rounded-xl shrink-0" />
                                <div className="flex-1 space-y-2">
                                    <Skeleton className="h-4 w-2/3 rounded" />
                                    <Skeleton className="h-3 w-1/3 rounded" />
                                </div>
                                <Skeleton className="h-5 w-16 rounded" />
                            </div>
                        ))}
                    </div>
                    <div className="space-y-3">
                        <Skeleton className="h-40 w-full rounded-2xl" />
                        <Skeleton className="h-10 w-full rounded-xl" />
                    </div>
                </div>
            </div>
        );
    }

    const subtotal = Math.round(cart.reduce((acc, item) => acc + (item.price * item.quantity), 0) * 100) / 100;
    const total = Math.round((subtotal - (appliedCoupon?.discount ?? 0) - (appliedPoints?.discount ?? 0)) * 100) / 100;
    // Minimum zamówienia grupy B2B (netto, bez dostawy) — backend i tak odrzuci mniejsze zamówienie.
    const b2bMissing = b2bMinOrder !== null && !isAdmin ? Math.round((b2bMinOrder - cartNetValue(cart)) * 100) / 100 : 0;
    const isVirtualOnlyCart = cart.length > 0 && cart.every(item => item.requires_shipping === false);

    return (
        <div className="container py-10 space-y-6">
            {removeIconHoverStyle && <style>{removeIconHoverStyle}</style>}
            {isAdmin && (
                <div className="mb-6 p-3 bg-blue-600 text-white rounded-xl text-xs font-bold uppercase tracking-wider flex items-center gap-2 shadow-sm">
                    <Info className="h-4 w-4 shrink-0" />
                    <span className="flex-1">
                        Podgląd CMS: {t('cart.title')} — {previewEmptyCart ? 'Pokazujesz stan pustego koszyka' : 'Pokazujesz przykładowy produkt w koszyku'}
                    </span>
                    <button
                        type="button"
                        onClick={() => setPreviewEmptyCart((v) => !v)}
                        className="shrink-0 bg-white/20 hover:bg-white/30 rounded-lg px-2 py-1 normal-case font-semibold tracking-normal"
                    >
                        {previewEmptyCart ? 'Pokaż koszyk z produktem' : 'Podgląd: pusty koszyk'}
                    </button>
                </div>
            )}
            <h1 className="text-3xl font-bold tracking-tight text-primary">{t('cart.title')}</h1>

            {(cart.length === 0 || (isAdmin && previewEmptyCart)) ? (
                <div className="text-center py-20 bg-muted/30 rounded-lg border-2 border-dashed border-muted">
                    <div className="flex justify-center mb-4 text-muted-foreground/50" style={settings.emptyCartIconColor ? { color: settings.emptyCartIconColor } : undefined}>
                        <ShoppingCart className="h-16 w-16" />
                    </div>
                    <p className="text-muted-foreground text-lg mb-6" style={settings.emptyCartTextColor ? { color: settings.emptyCartTextColor } : undefined}>
                        {settings.emptyCartText || t('cart.empty')}
                    </p>
                    <Link href={settings.emptyCartButtonUrl || '/produkty'}>
                        <Button
                            size="lg"
                            className="font-semibold"
                            style={settings.emptyCartButtonBg ? { backgroundColor: settings.emptyCartButtonBg, color: settings.emptyCartButtonTextColor ?? '#ffffff' } : undefined}
                        >
                            {settings.emptyCartButtonText || t('cart.continue_shopping')}
                        </Button>
                    </Link>
                </div>
            ) : (
                <div className={isAdmin && (viewport === 'mobile' || viewport === 'tablet') ? "grid grid-cols-1 gap-10" : "grid lg:grid-cols-3 gap-10"}>
                    <div className={isAdmin && (viewport === 'mobile' || viewport === 'tablet') ? "space-y-4" : "lg:col-span-2 space-y-4"}>
                        {cart.map((item) => {
                            const itemId = item.cartItemId ?? String(item.id);
                            const isRemoving = removingIds.has(itemId);
                            return (
                            <Card
                                key={itemId}
                                className={`overflow-hidden border shadow-sm bg-white transition-all duration-200 ${isRemoving ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}
                                style={productCardStyle}
                            >
                                <CardContent 
                                    className="p-4" 
                                    style={productCardContentStyle}
                                >
                                    {/* Desktop View */}
                                    <div className={isAdmin && viewport === 'mobile' ? "hidden" : "hidden sm:flex items-center w-full gap-6"}>
                                        <div className="h-24 w-24 bg-secondary rounded-md flex items-center justify-center text-primary font-bold text-xl relative overflow-hidden group border border-border/50 shrink-0" style={imagePlaceholderStyle}>
                                            {item.image ? (
                                                <Image
                                                    src={toOptimizableSrc(getAbsoluteMediaUrl(item.image))}
                                                    alt={item.name}
                                                    fill
                                                    sizes="96px"
                                                    className="absolute inset-0 w-full h-full object-cover transition-transform group-hover:scale-110 duration-500"
                                                />
                                            ) : (
                                                <>
                                                    <div className="absolute inset-0 bg-primary/10 group-hover:bg-primary/20 transition-colors" />
                                                    <span className="relative z-10 text-xs text-muted-foreground uppercase tracking-widest" style={imagePlaceholderTextStyle}>{t('cart.no_photo', 'Brak foto')}</span>
                                                </>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0 space-y-1">
                                            <h3
                                                className="font-bold text-lg cursor-pointer hover:text-primary transition-colors break-words"
                                                style={productTitleStyle}
                                            >
                                                <Link href={`/produkty/${item.slug || 'product-' + item.id}`}>{item.name}</Link>
                                            </h3>
                                            {settings.showAttributes !== false && item.attributes && Object.keys(item.attributes).length > 0 && (
                                                <div className="text-xs text-muted-foreground flex flex-wrap gap-x-2 gap-y-0.5">
                                                    {Object.entries(item.attributes).filter(([k]) => !(item.bundle_selection && k === 'Zestaw')).map(([k, v]: [string, any]) => (
                                                        <span key={k} className="bg-gray-100 px-1.5 py-0.5 rounded" style={attributeBadgeStyle}>{k}: {v}</span>
                                                    ))}
                                                </div>
                                            )}
                                            {item.bundle_selection && item.attributes?.Zestaw && (
                                                <BundleSelectionList parts={parseBundleSummary(item.attributes.Zestaw)} />
                                            )}
                                            <p className="text-sm text-muted-foreground font-medium" style={productTextStyle}>{formatPrice(item.price, currency)}{item.qty_unit ? ` / ${item.qty_unit}` : ''}</p>
                                        </div>
                                        {item.is_one_off ? (
                                            <span className="px-3 py-1.5 text-xs font-semibold bg-secondary text-muted-foreground rounded-md shrink-0 select-none">
                                                {t('cart.one_off_qty', '1 szt.')}
                                            </span>
                                        ) : (
                                            <div className="flex items-center gap-3 bg-secondary/50 p-1 rounded-md shrink-0" style={quantityWrapperStyle}>
                                                <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-background" style={quantityTextStyle} onClick={() => updateQuantity(item.cartItemId ?? String(item.id), -1)}>-</Button>
                                                <span className="min-w-8 px-1 text-center font-bold tab-nums whitespace-nowrap" style={quantityTextStyle}>{item.quantity}{item.qty_unit ? ` ${item.qty_unit}` : ''}</span>
                                                <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-background" style={quantityTextStyle} disabled={!!item.max_quantity && item.quantity >= item.max_quantity} onClick={() => updateQuantity(item.cartItemId ?? String(item.id), 1)}>+</Button>
                                            </div>
                                        )}
                                        <div className="text-right min-w-[100px] shrink-0">
                                            <p className="font-bold text-lg text-primary" style={productTitleStyle}>
                                                {formatPrice(item.price * item.quantity, currency)}
                                            </p>
                                        </div>
                                        <Button variant="ghost" size="icon" className={`text-muted-foreground hover:text-destructive hover:bg-destructive/10 shrink-0 ${removeIconCls}`} style={removeIconStyle} onClick={() => removeItem(item.cartItemId ?? String(item.id))}>
                                            <Trash className="h-4 w-4" />
                                        </Button>
                                    </div>

                                    {/* Mobile View */}
                                    <div className={isAdmin && viewport === 'mobile' ? "flex flex-col w-full gap-3" : "flex sm:hidden flex-col w-full gap-3"}>
                                        <div className="flex gap-3 items-start relative">
                                            <div className="h-20 w-20 bg-secondary rounded-md flex items-center justify-center text-primary font-bold text-base relative overflow-hidden group border border-border/50 shrink-0" style={imagePlaceholderStyle}>
                                                {item.image ? (
                                                    <Image
                                                        src={toOptimizableSrc(getAbsoluteMediaUrl(item.image))}
                                                        alt={item.name}
                                                        fill
                                                        sizes="80px"
                                                        className="absolute inset-0 w-full h-full object-cover transition-transform group-hover:scale-110 duration-500"
                                                    />
                                                ) : (
                                                    <>
                                                        <div className="absolute inset-0 bg-primary/10 group-hover:bg-primary/20 transition-colors" />
                                                        <span className="relative z-10 text-[10px] text-muted-foreground uppercase tracking-wider text-center" style={imagePlaceholderTextStyle}>{t('cart.no_photo', 'Brak foto')}</span>
                                                    </>
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0 pr-8 space-y-1">
                                                <h3
                                                    className="font-bold text-sm cursor-pointer hover:text-primary transition-colors break-words"
                                                    style={productTitleStyle}
                                                >
                                                    <Link href={`/produkty/${item.slug || 'product-' + item.id}`}>{item.name}</Link>
                                                </h3>
                                                {settings.showAttributes !== false && item.attributes && Object.keys(item.attributes).length > 0 && (
                                                    <div className="text-xs text-muted-foreground flex flex-wrap gap-1.5">
                                                        {Object.entries(item.attributes).filter(([k]) => !(item.bundle_selection && k === 'Zestaw')).map(([k, v]: [string, any]) => (
                                                            <span key={k} className="bg-gray-100 px-2 py-0.5 rounded leading-snug" style={attributeBadgeStyle}>{k}: {v}</span>
                                                        ))}
                                                    </div>
                                                )}
                                                {item.bundle_selection && item.attributes?.Zestaw && (
                                                    <BundleSelectionList parts={parseBundleSummary(item.attributes.Zestaw)} />
                                                )}
                                                <p className="text-xs text-muted-foreground font-semibold" style={productTextStyle}>{formatPrice(item.price, currency)}{item.qty_unit ? ` / ${item.qty_unit}` : ''}</p>
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className={`absolute -top-1.5 -right-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 h-8 w-8 rounded-full shrink-0 ${removeIconCls}`}
                                                style={removeIconStyle}
                                                onClick={() => removeItem(item.cartItemId ?? String(item.id))}
                                            >
                                                <Trash className="h-4 w-4" />
                                            </Button>
                                        </div>
                                        <div className="h-px bg-border/50 my-1" />
                                        <div className="flex justify-between items-center">
                                            {item.is_one_off ? (
                                                <span className="px-2.5 py-1.5 text-[10px] font-semibold bg-secondary text-muted-foreground rounded-md select-none">
                                                    {t('cart.one_off_qty', '1 szt.')}
                                                </span>
                                            ) : (
                                                <div className="flex items-center gap-2 bg-secondary/50 p-0.5 rounded-md" style={quantityWrapperStyle}>
                                                    <Button variant="ghost" size="icon" className="h-7 w-7 hover:bg-background" style={quantityTextStyle} onClick={() => updateQuantity(item.cartItemId ?? String(item.id), -1)}>-</Button>
                                                    <span className="min-w-6 px-1 text-center font-bold text-xs tab-nums whitespace-nowrap" style={quantityTextStyle}>{item.quantity}{item.qty_unit ? ` ${item.qty_unit}` : ''}</span>
                                                    <Button variant="ghost" size="icon" className="h-7 w-7 hover:bg-background" style={quantityTextStyle} disabled={!!item.max_quantity && item.quantity >= item.max_quantity} onClick={() => updateQuantity(item.cartItemId ?? String(item.id), 1)}>+</Button>
                                                </div>
                                            )}
                                            <div className="text-right">
                                                <p className="font-bold text-base text-primary" style={productTitleStyle}>
                                                    {formatPrice(item.price * item.quantity, currency)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                        })}

                        {/* Inline Upsell Products Section under left column */}
                        {(() => {
                            if (settings.showUpsellBlock === false) return null;

                            // Filter out products already in cart
                            const availableUpsellProducts = upsellProducts
                                .filter((prod: any) => !cart.some((cartItem: any) => cartItem.id === prod.id))
                                .slice(0, settings.upsellLimit !== undefined ? settings.upsellLimit : 12);

                            if (availableUpsellProducts.length === 0) return null;

                            const itemsPerPage = settings.upsellItemsPerPage !== undefined ? settings.upsellItemsPerPage : 3;
                            const totalPages = Math.ceil(availableUpsellProducts.length / itemsPerPage);
                            const safePage = Math.min(upsellPage, Math.max(0, totalPages - 1));
                            const currentProducts = availableUpsellProducts.slice(
                                safePage * itemsPerPage,
                                (safePage + 1) * itemsPerPage
                            );

                            const cols = settings.upsellColumns !== undefined ? settings.upsellColumns : 3;
                            const gridColsClass =
                                cols === 4
                                    ? 'grid-cols-2 sm:grid-cols-4'
                                    : cols === 2
                                    ? 'grid-cols-2 sm:grid-cols-2'
                                    : cols === 1
                                    ? 'grid-cols-1'
                                    : 'grid-cols-2 sm:grid-cols-3';

                            return (
                                <div className="mt-8 pt-6 border-t border-slate-200/80 space-y-4">
                                    <div className="flex items-center justify-between gap-4">
                                        <div>
                                            <h3
                                                className="font-extrabold tracking-tight"
                                                style={{
                                                    color: settings.upsellTitleColor || '#0f172a',
                                                    fontSize: `${settings.upsellTitleSize || 18}px`,
                                                }}
                                            >
                                                {settings.upsellTitle || t('cart.upsell_title', 'Dobierz do zamówienia')}
                                            </h3>
                                            <p
                                                className="font-medium"
                                                style={{
                                                    color: settings.upsellSubtitleColor || '#64748b',
                                                    fontSize: `${settings.upsellSubtitleSize || 12}px`,
                                                }}
                                            >
                                                {settings.upsellSubtitle || t('cart.upsell_subtitle', 'Inni dorzucają też te produkty.')}
                                            </p>
                                        </div>

                                        {/* Pagination Controls */}
                                        {totalPages > 1 && (
                                            <div className="flex items-center gap-2 shrink-0">
                                                <div className="flex items-center gap-1 mr-1">
                                                    {Array.from({ length: totalPages }).map((_, idx) => (
                                                        <button
                                                            key={idx}
                                                            type="button"
                                                            onClick={() => setUpsellPage(idx)}
                                                            className="w-2 h-2 rounded-full transition-all"
                                                            style={{
                                                                backgroundColor:
                                                                    idx === safePage
                                                                        ? settings.upsellDotActiveColor || '#2563eb'
                                                                        : settings.upsellDotColor || '#cbd5e1',
                                                                transform: idx === safePage ? 'scale(1.25)' : 'scale(1)',
                                                            }}
                                                            aria-label={t('cart.pagination_page', 'Strona {n}').replace('{n}', String(idx + 1))}
                                                        />
                                                    ))}
                                                </div>
                                                <button
                                                    type="button"
                                                    disabled={safePage === 0}
                                                    onClick={() => setUpsellPage((p) => Math.max(0, p - 1))}
                                                    className="w-7 h-7 rounded-full border border-slate-200 bg-white flex items-center justify-center text-slate-600 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-xs"
                                                    style={{
                                                        color: settings.upsellDotActiveColor || '#2563eb',
                                                    }}
                                                >
                                                    <ChevronLeft className="w-4 h-4" />
                                                </button>
                                                <button
                                                    type="button"
                                                    disabled={safePage >= totalPages - 1}
                                                    onClick={() => setUpsellPage((p) => Math.min(totalPages - 1, p + 1))}
                                                    className="w-7 h-7 rounded-full border border-slate-200 bg-white flex items-center justify-center text-slate-600 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-xs"
                                                    style={{
                                                        color: settings.upsellDotActiveColor || '#2563eb',
                                                    }}
                                                >
                                                    <ChevronRight className="w-4 h-4" />
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    <div className={`grid ${gridColsClass} gap-3`}>
                                        {currentProducts.map((prod: any) => {
                                            const isHovered = hoveredUpsellId === prod.id;
                                            const mainImg = prod.images?.find((img: any) => img.is_main) || prod.images?.[0];
                                            const rawImgPath =
                                                mainImg?.path ||
                                                mainImg?.thumbnail_path ||
                                                prod.image_url ||
                                                prod.image ||
                                                prod.image_path ||
                                                prod.main_image?.path ||
                                                prod.main_image_url ||
                                                prod.thumbnail_path ||
                                                prod.thumbnail;
                                            const imageUrl = rawImgPath ? toOptimizableSrc(getAbsoluteMediaUrl(rawImgPath)) : null;

                                            return (
                                                <div
                                                    key={prod.id}
                                                    className="flex flex-col items-center text-center shadow-xs transition-all duration-200"
                                                    style={{
                                                        backgroundColor: settings.upsellCardBg || '#ffffff',
                                                        borderColor: settings.upsellCardBorderColor || '#e2e8f0',
                                                        borderWidth: `${settings.upsellCardBorderWidth !== undefined ? settings.upsellCardBorderWidth : 1}px`,
                                                        borderStyle: 'solid',
                                                        borderRadius: `${settings.upsellCardBorderRadius !== undefined ? settings.upsellCardBorderRadius : 16}px`,
                                                        padding: `${settings.upsellCardPadding !== undefined ? settings.upsellCardPadding : 12}px`,
                                                    }}
                                                >
                                                    <div className="w-20 h-20 relative mb-2 overflow-hidden rounded-xl bg-slate-50 flex items-center justify-center">
                                                        {imageUrl ? (
                                                            <img
                                                                src={imageUrl}
                                                                alt={prod.name}
                                                                className="w-full h-full object-cover"
                                                            />
                                                        ) : (
                                                            <ShoppingCart className="w-6 h-6 text-slate-300" />
                                                        )}
                                                    </div>
                                                    <span
                                                        className="font-bold text-xs break-words leading-tight mb-1"
                                                        style={{ color: settings.upsellProductNameColor || '#0f172a' }}
                                                    >
                                                        {prod.name}
                                                    </span>
                                                    <span
                                                        className="font-black text-xs mb-2"
                                                        style={{ color: settings.upsellProductPriceColor || '#334155' }}
                                                    >
                                                        {formatPrice(prod.sales_price ?? prod.price, currency)}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleQuickAddUpsell(prod)}
                                                        onMouseEnter={() => setHoveredUpsellId(prod.id)}
                                                        onMouseLeave={() => setHoveredUpsellId(null)}
                                                        className="w-full py-2 px-3 font-extrabold text-xs transition-all active:scale-95 shadow-xs"
                                                        style={{
                                                            backgroundColor: isHovered
                                                                ? settings.upsellBtnHoverBg || '#e6bb00'
                                                                : settings.upsellBtnBg || '#FFCF00',
                                                            color: settings.upsellBtnTextColor || '#0f172a',
                                                            borderRadius: `${settings.upsellBtnBorderRadius !== undefined ? settings.upsellBtnBorderRadius : 9999}px`,
                                                        }}
                                                    >
                                                        {prod.product_type === 'ticket' && prod.has_seat_map
                                                            ? t('product.select_seat', 'Wybierz miejsce')
                                                            : prod.product_type === 'pick_pack'
                                                                ? (prod.pickpack_config?.cta_label || t('product.configure_bundle', 'Skonfiguruj zestaw'))
                                                                : prod.product_type === 'configurable'
                                                                    ? t('product.configure', 'Konfiguruj')
                                                                    : usesConfigurator(prod)
                                                                    ? t('product.select_options', 'Wybierz opcje')
                                                                    : prod.product_type === 'variable'
                                                                        ? t('product.select_options', 'Wybierz opcje')
                                                                        : (settings.upsellBtnText || t('cart.upsell_add_btn', '+ Dodaj'))}
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })()}
                    </div>

                    <div>
                        <Card
                            className={`sticky top-24 border shadow-lg ${!settings.summaryBg ? 'bg-primary/5' : ''}`}
                            style={summaryCardStyle}
                        >
                            <CardHeader style={settings.summaryPadding !== undefined ? { paddingBottom: 0, paddingLeft: settings.summaryPadding, paddingRight: settings.summaryPadding, paddingTop: settings.summaryPadding } : undefined}>
                                <CardTitle className="text-primary" style={summaryTitleStyle}>{t('cart.summary')}</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-6" style={summaryCardContentStyle}>
                                {/* Free Delivery Progress Widget */}
                                {/* free_delivery_threshold_display is the threshold already converted server-side
                                    into the customer's display currency; the merchant enters the raw value in
                                    their own shop currency (siteConfig.free_delivery_threshold), which must never
                                    be compared/shown directly against a subtotal in a different currency. */}
                                {(() => {
                                    if (settings.showFreeShippingBar === false) return null;
                                    const rawThreshold = siteConfig?.free_delivery_threshold_display ?? siteConfig?.free_delivery_threshold ?? null;
                                    if (isVirtualOnlyCart || rawThreshold === null || rawThreshold === undefined || !(Number(rawThreshold) > 0)) return null;
                                    const freeDeliveryThreshold = Math.round(Number(rawThreshold) * 100) / 100;
                                    const missingAmount = Math.max(0, Math.round((freeDeliveryThreshold - total) * 100) / 100);
                                    const isEligible = total >= freeDeliveryThreshold || missingAmount === 0 || !!appliedCoupon?.freeShipping;

                                    return (
                                    <div className="space-y-2 bg-white/60 dark:bg-slate-900/40 p-4 rounded-xl border border-primary/10 shadow-sm" style={freeShippingCardStyle}>
                                        {isEligible ? (
                                            <div className="space-y-1.5">
                                                <div
                                                    className={settings.freeShippingCompleteColor ? "flex items-center gap-2 font-extrabold text-xs uppercase tracking-wider" : "flex items-center gap-2 text-green-600 font-extrabold text-xs uppercase tracking-wider"}
                                                    style={settings.freeShippingCompleteColor ? { color: settings.freeShippingCompleteColor } : undefined}
                                                >
                                                    <span className="flex h-2 w-2 relative">
                                                        <span
                                                            className={settings.freeShippingCompleteColor ? "animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" : "animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"}
                                                            style={settings.freeShippingCompleteColor ? { backgroundColor: settings.freeShippingCompleteColor } : undefined}
                                                        ></span>
                                                        <span
                                                            className={settings.freeShippingCompleteColor ? "relative inline-flex rounded-full h-2 w-2" : "relative inline-flex rounded-full h-2 w-2 bg-green-500"}
                                                            style={settings.freeShippingCompleteColor ? { backgroundColor: settings.freeShippingCompleteColor } : undefined}
                                                        ></span>
                                                    </span>
                                                    {t('cart.free_shipping_eligible', 'Kwalifikujesz się do darmowej dostawy!')}
                                                </div>
                                                <div className="w-full bg-slate-100 dark:bg-slate-800 h-2 rounded-full overflow-hidden" style={freeShippingTrackStyle}>
                                                    <div
                                                        className={settings.freeShippingCompleteColor ? "h-full rounded-full" : "h-full bg-green-500 rounded-full"}
                                                        style={{ width: '100%', backgroundColor: settings.freeShippingCompleteColor || undefined }}
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="space-y-2">
                                                <div className="text-xs font-semibold text-slate-600 dark:text-slate-300" style={freeShippingTextStyle}>
                                                    {t('cart.free_shipping_hint', 'Darmowa dostawa od')} <span className="font-extrabold text-primary">{formatPrice(freeDeliveryThreshold, currency)}</span>
                                                </div>
                                                <div className="text-xs text-slate-500" style={freeShippingTextStyle}>
                                                    {t('cart.free_shipping_missing', 'Brakuje Ci jeszcze')} <span className="font-bold" style={{ color: settings.freeShippingBarColorTo || '#2E7D32' }}>{formatPrice(missingAmount, currency)}</span>!
                                                </div>
                                                <div className="w-full bg-slate-100 dark:bg-slate-800 h-2 rounded-full overflow-hidden" style={freeShippingTrackStyle}>
                                                    <div
                                                        className={settings.freeShippingBarColorFrom ? "h-full rounded-full transition-all duration-500" : "h-full bg-gradient-to-r from-amber-400 to-[#2E7D32] rounded-full transition-all duration-500"}
                                                        style={{
                                                            width: `${Math.min((total / freeDeliveryThreshold) * 100, 100)}%`,
                                                            background: settings.freeShippingBarColorFrom
                                                                ? `linear-gradient(90deg, ${settings.freeShippingBarColorFrom}, ${settings.freeShippingBarColorTo || settings.freeShippingBarColorFrom})`
                                                                : undefined,
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    );
                                })()}

                                <div className="space-y-2 text-sm" style={summaryTextStyle}>
                                    <div className="flex justify-between text-muted-foreground" style={{ color: settings.summaryTextColor }}>
                                        <span>{t('cart.subtotal')}</span>
                                        <span style={{ color: settings.summaryTitleColor || settings.summaryTextColor }}>{formatPrice(subtotal, currency)}</span>
                                    </div>
                                    {appliedCoupon && appliedCoupon.discount > 0 && (
                                        <div className="flex justify-between text-green-600 font-medium">
                                            <span className="flex items-center gap-1">
                                                <Tag className="h-3 w-3" />
                                                {appliedCoupon.code}
                                            </span>
                                            <span>-{formatPrice(appliedCoupon.discount, currency)}</span>
                                        </div>
                                    )}
                                    {appliedCoupon?.gift && (
                                        <div className="flex justify-between gap-3 text-green-600 font-medium">
                                            <span className="flex items-center gap-1 min-w-0">
                                                <Gift className="h-3 w-3 shrink-0" />
                                                <span className="truncate">{t('cart.coupon_gift', 'Gratis')}: {appliedCoupon.gift.name}</span>
                                            </span>
                                            <span>{formatPrice(0, currency)}</span>
                                        </div>
                                    )}
                                    {appliedPoints && (
                                        <div className="flex justify-between text-green-600 font-medium">
                                            <span className="flex items-center gap-1">
                                                <Tag className="h-3 w-3" />
                                                {appliedPoints.points} {t('cart.points_unit', 'pkt')}
                                            </span>
                                            <span>-{formatPrice(appliedPoints.discount, currency)}</span>
                                        </div>
                                    )}
                                    {!isVirtualOnlyCart && (
                                        <div className="flex justify-between text-muted-foreground" style={{ color: settings.summaryTextColor }}>
                                            <span>{t('cart.shipping')}</span>
                                            <span className="font-medium text-foreground" style={{ color: settings.summaryTitleColor || settings.summaryTextColor }}>
                                                {(() => {
                                                    if (appliedCoupon?.freeShipping) return t('checkout.free', 'Bezpłatnie');
                                                    const rawThreshold = siteConfig?.free_delivery_threshold_display ?? siteConfig?.free_delivery_threshold ?? null;
                                                    if (rawThreshold === null || rawThreshold === undefined || !(Number(rawThreshold) > 0)) return t('cart.shipping_calculated_checkout', 'wybierana w kasie');
                                                    const freeDeliveryThreshold = Math.round(Number(rawThreshold) * 100) / 100;
                                                    const isFreeDelivery = total >= freeDeliveryThreshold;

                                                    if (isFreeDelivery) return t('checkout.free', 'Bezpłatnie');

                                                    return t('cart.shipping_calculated_checkout', 'wybierana w kasie');
                                                })()}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Coupon input */}
                                {settings.showCoupon === false ? null : appliedCoupon ? (
                                    <div
                                        className={settings.couponBadgeBg ? "flex items-center justify-between rounded-md px-3 py-2 text-sm border" : "flex items-center justify-between bg-green-50 border border-green-200 rounded-md px-3 py-2 text-sm"}
                                        style={settings.couponBadgeBg ? { backgroundColor: settings.couponBadgeBg, borderColor: settings.couponBadgeTextColor || undefined } : undefined}
                                    >
                                        <span
                                            className={settings.couponBadgeTextColor ? "flex items-center gap-2 font-medium" : "flex items-center gap-2 text-green-700 font-medium"}
                                            style={settings.couponBadgeTextColor ? { color: settings.couponBadgeTextColor } : undefined}
                                        >
                                            <Tag className="h-4 w-4" />
                                            {appliedCoupon.code}
                                        </span>
                                        <button onClick={removeCoupon} className="text-green-500 hover:text-red-500 transition-colors">
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="space-y-1">
                                        <div className="flex gap-2">
                                            <Input
                                                placeholder={t('cart.discount_code')}
                                                value={couponInput}
                                                onChange={e => setCouponInput(e.target.value.toUpperCase())}
                                                onKeyDown={e => e.key === 'Enter' && applyCoupon()}
                                                className="text-sm h-9 bg-white"
                                                style={couponInputStyle}
                                            />
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => applyCoupon()}
                                                disabled={couponLoading || !couponInput.trim()}
                                                className={settings.couponButtonBg ? undefined : 'bg-white'}
                                                style={settings.couponButtonBg ? { backgroundColor: settings.couponButtonBg, color: settings.couponButtonTextColor || undefined } : undefined}
                                            >
                                                {couponLoading ? '...' : t('cart.apply_code')}
                                            </Button>
                                        </div>
                                        {couponError && <p className="text-xs text-red-500" style={couponErrorStyle}>{couponError}</p>}
                                    </div>
                                )}

                                {/* Loyalty earn preview — distinct from the redemption widget below */}
                                {siteConfig?.loyalty_enabled && settings.showLoyaltyPreview !== false && (
                                    <LoyaltyEarnPreview
                                        amount={Math.max(0, subtotal - (appliedCoupon?.discount ?? 0))}
                                        pointsRedeemed={appliedPoints?.points || 0}
                                        label={settings.loyaltyPreviewLabel || t('cart.points_earn_preview', 'Zdobędziesz za to zamówienie')}
                                        style={{
                                            color: settings.loyaltyPreviewTextColor || undefined,
                                            backgroundColor: settings.loyaltyPreviewBgColor || undefined,
                                            borderColor: settings.loyaltyPreviewBgColor ? 'transparent' : undefined,
                                            fontSize: settings.loyaltyPreviewFontSize ? `${settings.loyaltyPreviewFontSize}px` : undefined,
                                        }}
                                    />
                                )}

                                {/* Loyalty points redemption */}
                                {siteConfig?.loyalty_enabled && session?.user && loyaltyBalance > 0 && (
                                    appliedPoints ? (
                                        <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-sm">
                                            <span className="flex items-center gap-2 text-emerald-700 font-medium">
                                                <Tag className="h-4 w-4" />
                                                {appliedPoints.points} {t('cart.points_unit', 'pkt')}
                                            </span>
                                            <button onClick={removePoints} className="text-emerald-500 hover:text-red-500 transition-colors">
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="space-y-1">
                                            <p className="text-xs text-muted-foreground">
                                                {t('cart.points_balance', 'Masz')} {loyaltyBalance} {t('cart.points_unit', 'pkt')} ({t('cart.points_min_redeem', 'min. do wymiany')}: {siteConfig?.loyalty_min_redeem_points ?? 100})
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                {t('cart.points_max_usable', 'Przy tej kwocie zamówienia wykorzystasz maksymalnie')} {Math.min(loyaltyBalance, Math.floor((subtotal - (appliedCoupon?.discount ?? 0)) / (siteConfig?.loyalty_point_value || 1)))} {t('cart.points_unit', 'pkt')}
                                            </p>
                                            <div className="flex gap-2">
                                                <Input
                                                    type="number"
                                                    min={siteConfig?.loyalty_min_redeem_points ?? 100}
                                                    max={loyaltyBalance}
                                                    placeholder={t('cart.points_placeholder', 'Liczba punktów')}
                                                    value={pointsInput}
                                                    onChange={e => setPointsInput(e.target.value)}
                                                    onKeyDown={e => e.key === 'Enter' && applyPoints()}
                                                    className="text-sm h-9 bg-white"
                                                />
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={applyPoints}
                                                    disabled={pointsLoading || !pointsInput.trim()}
                                                    className="bg-white"
                                                >
                                                    {pointsLoading ? '...' : t('cart.apply_code')}
                                                </Button>
                                            </div>
                                            {pointsError && <p className="text-xs text-red-500">{pointsError}</p>}
                                        </div>
                                    )
                                )}

                                {cart.some((i: any) => i.product_type === 'service') && cart.some((i: any) => i.product_type !== 'service') && (
                                    // Usługi logistyczne wykluczają łączenie ze zwykłymi produktami — backend
                                    // (POST /orders) jest ostatecznym źródłem prawdy, to tylko wcześniejsza
                                    // informacja w koszyku. Patrz sekcja 5.1 SPEC_SERVICE_LOGISTICS_SUBSCRIPTIONS.md.
                                    <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-xs">
                                        <span>{t('checkout.error_mixed_service_cart', 'Usługi logistyczne wymagają osobnego zamówienia — nie można ich łączyć ze zwykłymi produktami w koszyku.')}</span>
                                    </div>
                                )}

                                <div className="border-t border-primary/20 pt-4 flex flex-col gap-1">
                                    <div className="flex justify-between font-bold text-xl text-foreground" style={summaryTextStyle}>
                                        <span>{t('cart.total')}</span>
                                        <span className="text-primary" style={summaryTitleStyle}>{formatPrice(total, currency)}</span>
                                    </div>
                                    {!isVirtualOnlyCart && (
                                        <p className="text-xs text-muted-foreground text-right" style={{ color: settings.summaryTextColor, opacity: 0.8 }}>
                                            + {t('cart.shipping_hint', 'koszt dostawy obliczony w kolejnym kroku')}
                                        </p>
                                    )}
                                </div>
                                {b2bMissing > 0 && (
                                    <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-xs" role="status">
                                        <Info className="h-4 w-4 shrink-0 mt-0.5" />
                                        <span>
                                            {t('cart.b2b_min_order', 'Minimalna wartość zamówienia dla Twojej firmy to {min} netto (bez dostawy). Brakuje {missing} netto.')
                                                .replace('{min}', formatPrice(b2bMinOrder ?? 0, currency))
                                                .replace('{missing}', formatPrice(b2bMissing, currency))}
                                        </span>
                                    </div>
                                )}
                                {b2bMissing > 0 ? (
                                    <Button className="w-full text-lg py-6 font-bold" size="lg" style={summaryBtnStyle} disabled>
                                        {settings.summaryBtnText || t('cart.checkout_btn')}
                                    </Button>
                                ) : (
                                 <Link
                                    href={tPath(`/zamowienie${buildCheckoutQuery(appliedCoupon, appliedPoints)}`)}
                                    className="block"
                                    onClick={() => trackEvent('initiate_checkout')}
                                >
                                    <Button className="w-full text-lg py-6 font-bold shadow-lg shadow-primary/20" size="lg" style={summaryBtnStyle}>
                                        {settings.summaryBtnText || t('cart.checkout_btn')}
                                    </Button>
                                </Link>
                                )}

                                {settings.showLeasingOption !== false && siteConfig?.leasenow_enabled && (
                                    leasenowCartData?.availability ? (
                                        <div className="mt-3 flex flex-col items-center gap-1.5 pt-2 border-t border-slate-100">
                                            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t('cart.leasing_installment_label', 'Płatność ratalna B2B:')}</span>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (leasenowCartData?.redirectUrl) {
                                                        window.open(leasenowCartData.redirectUrl, '_blank');
                                                    } else {
                                                        const extraParams = buildCheckoutQuery(appliedCoupon, appliedPoints).replace(/^\?/, '&');
                                                        router.push(tPath(`/zamowienie?paymentMethod=leasenow${extraParams}`));
                                                    }
                                                }}
                                                aria-label="ING Lease Now"
                                                className="block w-[260px] max-w-full transition-transform hover:scale-[1.02] active:scale-[0.98]"
                                            >
                                                <img
                                                    alt="ING Lease Now"
                                                    className="leasenow_button-image"
                                                    src="https://leasenow.pl/assets/ecommerce/images/leasing_button.png"
                                                    style={{ display: 'block', width: '100%', height: 'auto' }}
                                                />
                                            </button>
                                        </div>
                                    ) : (leasenowCartData?.missingNetAmount > 0 && leasenowCartData?.products?.some((p: any) => p.availability)) ? (
                                        <div className="mt-3 p-3 bg-amber-50/80 rounded-xl border border-amber-200/80 text-left space-y-1" style={leasingBoxStyle}>
                                            <div className="flex items-center gap-1.5 text-amber-800 font-bold text-xs" style={leasingLabelStyle}>
                                                <Building2 className="h-4 w-4 shrink-0" />
                                                <span>{t('cart.leasing_b2b_title', 'Leasing B2B (od 1000 zł netto)')}</span>
                                            </div>
                                            <p className="text-[11px] text-slate-600 leading-tight" style={leasingTextStyle}>
                                                {t('cart.leasing_b2b_missing_prefix', 'Do leasingu B2B brakuje jeszcze')} <strong className="text-amber-800 font-bold" style={leasingLabelStyle}>{(leasenowCartData.missingNetAmount || 0).toFixed(2)} PLN netto</strong>. {t('cart.leasing_b2b_missing_suffix', 'Dodaj kolejne towary do koszyka.')}
                                            </p>
                                        </div>
                                    ) : null
                                )}
                            </CardContent>
                        </Card>

                    </div>
                </div>
            )}
        </div>
    );
}
